/**
 * Сессия режима «Предложения».
 *
 * Предложение проходится за один прогон: цикла Memory Loop здесь нет — он уже
 * отработал на уровне слова, а этот режим проверяет, держится ли навык в
 * связном тексте (ТЗ §6, режим 5; §7.6).
 */

import type { MasteryUnitId, UnitOutcome } from '@type2x/shared-contracts';
import { EMPTY_METRICS } from '@type2x/shared-contracts';
import { mergeMetrics, mergeObservations } from '@type2x/typing-engine';

import type {
  SentenceDrill,
  SentenceRun,
  SentenceSnapshot,
  SentenceTask,
  SentencesSession,
} from '../contracts/sentences.js';
import { SENTENCE_HINT_LEVEL } from '../contracts/sentences.js';

/** Собирает сессию из подобранных предложений. */
export function startSentencesSession(
  snapshot: SentenceSnapshot,
  drills: readonly SentenceDrill[],
): SentencesSession {
  return {
    language: snapshot.language,
    layoutId: snapshot.layoutId,
    drills,
    currentIndex: 0,
    finished: drills.length === 0,
    metrics: EMPTY_METRICS,
    errors: [],
  };
}

/** Предложение, которое пользователь набирает сейчас. */
export function currentSentence(session: SentencesSession): SentenceDrill | null {
  if (session.finished) return null;
  return session.drills[session.currentIndex] ?? null;
}

/** Предложения, до которых сессия ещё не дошла. */
export function remainingSentences(session: SentencesSession): SentenceDrill[] {
  return session.drills.filter((drill) => !drill.done);
}

/** Задание текущего предложения либо null, если набирать уже нечего. */
export function sentenceTask(session: SentencesSession): SentenceTask | null {
  const drill = currentSentence(session);
  if (drill === null) return null;

  return {
    text: drill.text,
    word: drill.word,
    reason: drill.reason,
    hintLevel: SENTENCE_HINT_LEVEL,
  };
}

/**
 * Засчитывает прогон предложения и переходит к следующему.
 *
 * Предложение не повторяется при ошибке: работа над отдельным словом — дело
 * режима «Мои ошибки», а здесь ошибки лишь уточняют оценку освоенности.
 *
 * @param session текущая сессия;
 * @param run прогон предложения.
 */
export function advanceSentences(session: SentencesSession, run: SentenceRun): SentencesSession {
  const drill = currentSentence(session);
  if (drill === null) return session;

  const passed: SentenceDrill = {
    ...drill,
    done: true,
    // Наблюдение относится к слову, ради которого предложение подобрано;
    // предложение переноса ничьей статистики не меняет.
    observed:
      drill.unitId === null ? drill.observed : mergeObservations(drill.observed, run.observation),
  };

  const drills = session.drills.map((item, index) =>
    index === session.currentIndex ? passed : item,
  );
  const next = drills.findIndex((item) => !item.done);

  return {
    ...session,
    drills,
    currentIndex: next === -1 ? session.currentIndex : next,
    finished: next === -1,
    metrics: mergeMetrics(session.metrics, run.metrics),
    errors: [...session.errors, ...run.errors],
  };
}

/** Агрегаты по единицам сессии для отправки на сервер. */
export function collectSentenceOutcomes(session: SentencesSession): UnitOutcome[] {
  return session.drills
    .filter((drill) => drill.unitId !== null && drill.observed.attempts > 0)
    .map((drill) => ({
      unitId: drill.unitId as MasteryUnitId,
      attempts: drill.observed.attempts,
      errors: drill.observed.errors,
      medianLatencyMs: drill.observed.medianLatencyMs,
      corrections: drill.observed.corrections,
    }));
}
